import * as H from "history";
import queryString from "query-string";

import { ReleaseStage } from "hooks/tivoApps";
import {
  TiVoAppVariant,
  TiVoAssets,
  TiVoPresentationAttributes,
} from "hooks/tivoApps/types";
import { ApiQueryParams } from "types/api";
import { parseContainsQueryParam } from "utils/url";

import {
  IMAGE_ENVS,
  IMAGES,
  PAGE_LIMIT,
  PRESENTATION_BOOLEAN_KEYS,
} from "./constants";

const MAX_RELEASE_STAGE_NUMBER = 3;
const RELEASE_STAGE_TAB_LABELS: Record<ReleaseStage, string> = {
  rs1: "Integration (rs1)",
  rs2: "Staging (rs2)",
  rs3: "Production (rs3)",
};

export function getReleaseStageTabLabel(releaseStage: ReleaseStage) {
  return RELEASE_STAGE_TAB_LABELS[releaseStage];
}

export function getNextReleaseStage(releaseStages: ReleaseStage[]) {
  const rsNumber = releaseStages.reduce((acc, cur) => {
    if (!cur.startsWith("rs")) {
      return acc;
    }
    const val = Number(cur.substring(2));
    if (isNaN(val) || val < acc) {
      return acc;
    }
    return val;
  }, 0);
  if (rsNumber >= MAX_RELEASE_STAGE_NUMBER) {
    return undefined;
  }
  return `rs${rsNumber + 1}` as ReleaseStage;
}

export function getNextReleaseStages(
  releaseStages: ReleaseStage[]
): ReleaseStage[] {
  const result: ReleaseStage[] = [];
  for (let i = 0; i < MAX_RELEASE_STAGE_NUMBER; i++) {
    const nextReleaseStage = getNextReleaseStage([...releaseStages, ...result]);
    if (!nextReleaseStage) {
      break;
    }
    result.push(nextReleaseStage);
  }
  return result;
}

export function toJsonString(data: unknown) {
  if (data === undefined) {
    return "";
  }
  return JSON.stringify(data, undefined, 2);
}

export function getImageUrlFromAssets(
  assets: TiVoAssets | Record<string, string> | undefined,
  type: IMAGES
) {
  if (!assets) {
    return "";
  }

  const tivoAssets = assets as TiVoAssets;
  return (
    tivoAssets[IMAGE_ENVS.PROD]?.[type]?.url ||
    tivoAssets[IMAGE_ENVS.STAGING]?.[type]?.url ||
    ""
  );
}

function getSingleValue<T>(value: T | T[]) {
  return Array.isArray(value) ? value[0] : value;
}

export function parseLocationToQueryParams(
  location: H.Location,
  limit: number = PAGE_LIMIT
) {
  const { contains, page, search, order, sort_by } = queryString.parse(
    location.search
  );

  const containsStr = getSingleValue(contains) ?? "";
  // 0 is not a valid page.
  const pageNum = Number(getSingleValue(page)) || 1;
  const searchStr = getSingleValue(search);
  const orderStr = getSingleValue(order);
  const sortByStr = getSingleValue(sort_by);

  let queryParams: ApiQueryParams = {
    ...parseContainsQueryParam(containsStr),
    limit,
    page: pageNum,
  };

  if (orderStr) {
    queryParams = {
      ...queryParams,
      order: (orderStr === "DESC" ? "-" : "") + sortByStr,
    };
  }

  if (searchStr) {
    queryParams = {
      ...queryParams,
      label: searchStr,
    };
  }
  return queryParams;
}

/**
 * Filters out null/undefined and returns only present boolean presentation attributes.
 */
export function getPresentationBooleanEntries(
  attrs: Record<string, unknown>
): { key: keyof TiVoPresentationAttributes; value: boolean }[] {
  return PRESENTATION_BOOLEAN_KEYS.reduce<
    { key: keyof TiVoPresentationAttributes; value: boolean }[]
  >((acc, key) => {
    const value = attrs[key];
    if (value === true || value === false) {
      acc.push({ key, value });
    }
    return acc;
  }, []);
}

export function sortVariantByCreationDate(
  a: TiVoAppVariant,
  b: TiVoAppVariant
) {
  const timeA = new Date(a?.creationDate || 0).getTime();
  const timeB = new Date(b?.creationDate || 0).getTime();
  // desc order
  if (timeA < timeB) {
    return 1;
  } else if (timeA > timeB) {
    return -1;
  }
  return 0;
}
